import React, {useRef, useState, useEffect, useCallback} from 'react';
import {Text, View, StyleSheet, TouchableOpacity, FlatList} from 'react-native';
import Product from './Product/Product';
import {styles} from './styles';
import {ISegment} from '../../../../types/Filter';
import {useIsDesktop} from '../../../../hooks/useIsDesktop';

interface IFeaturedItem {
  show: boolean;
  imageUrl: string;
  title: string;
  onPress: () => void;
}

interface IProductsCategories {
  segments: ISegment[];
  returnCategory: (category: string) => void;
  hasFetchedCategories?: boolean;
  featuredItem?: IFeaturedItem;
  offersItem?: IFeaturedItem;
}

const SkeletonCategories = ({isDesktop = false}: {isDesktop?: boolean}) => {
  const skeletonData = Array(isDesktop ? 8 : 4).fill(null);
  return (
    <View style={styles.container}>
      <FlatList
        horizontal
        numColumns={undefined}
        data={skeletonData}
        renderItem={() => (
          <View style={skeletonStyles.skeletonItem}>
            <View style={skeletonStyles.skeletonCircle} />
            <View style={skeletonStyles.skeletonText} />
          </View>
        )}
        keyExtractor={(_, index) => index.toString()}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{paddingHorizontal: 16}}
      />
    </View>
  );
};

const ProductsCategories = ({
  segments,
  returnCategory,
  hasFetchedCategories = false,
  featuredItem,
  offersItem,
}: IProductsCategories) => {
  const isDesktop = useIsDesktop();
  const flatListRef = useRef<FlatList>(null);
  const [scrollOffset, setScrollOffset] = useState(0);
  const SCROLL_STEP = 300;

  const scrollLeft = () => {
    const next = Math.max(0, scrollOffset - SCROLL_STEP);
    flatListRef.current?.scrollToOffset({offset: next, animated: true});
  };

  const scrollRight = () => {
    flatListRef.current?.scrollToOffset({
      offset: scrollOffset + SCROLL_STEP,
      animated: true,
    });
  };

  // Combinar o item de featured/ofertas (se existirem e show=true) com os segments
  const allItems = React.useMemo(() => {
    const items = [...segments];
    // Adicionar offers primeiro se existir (fica mais à esquerda quando unshift usado)
    if (offersItem && offersItem.show) {
      items.unshift({
        id: -2,
        name: offersItem.title,
        url: offersItem.imageUrl,
        category_id: null,
        isOffers: true,
      } as any);
    }
    // Adicionar featured depois (mantém ordem previsível)
    if (featuredItem && featuredItem.show) {
      items.unshift({
        id: -1,
        name: featuredItem.title,
        url: featuredItem.imageUrl,
        category_id: null,
        isFeatured: true,
      } as any);
    }
    return items;
  }, [segments, featuredItem, offersItem]);

  // drag-to-scroll com mouse no desktop web
  const dragState = useRef({active: false, startX: 0, startScroll: 0});

  const getScrollNode = useCallback((): HTMLElement | null => {
    const node = (flatListRef.current as any)?._listRef?._scrollRef;
    return node?._nativeTag ? null : node ?? null;
  }, []);

  useEffect(() => {
    if (!isDesktop) return;

    let el: HTMLElement | null = null;
    let cleanup: (() => void) | null = null;

    const attach = () => {
      el = getScrollNode();
      if (!el) return;

      const onMouseDown = (e: MouseEvent) => {
        dragState.current = {active: true, startX: e.pageX, startScroll: el!.scrollLeft};
        el!.style.cursor = 'grabbing';
        (el!.style as any).userSelect = 'none';
      };
      const onMouseMove = (e: MouseEvent) => {
        if (!dragState.current.active) return;
        el!.scrollLeft = dragState.current.startScroll + (dragState.current.startX - e.pageX);
      };
      const onMouseUp = () => {
        dragState.current.active = false;
        el!.style.cursor = 'grab';
        (el!.style as any).userSelect = '';
      };

      el.style.cursor = 'grab';
      el.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);

      cleanup = () => {
        el!.removeEventListener('mousedown', onMouseDown);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
    };

    const timer = setTimeout(attach, 100);
    return () => {
      clearTimeout(timer);
      cleanup?.();
    };
  }, [isDesktop, getScrollNode, allItems]);

  const renderItem = ({item, index}: {item: any; index: number}) => {
    const isFeaturedItem = !!item.isFeatured;
    const isOffersItem = !!item.isOffers;

    return (
      <Product
        key={item.id ?? index}
        uri={item.url}
        title={item.name}
        onPress={() => {
          if (isFeaturedItem && featuredItem) {
            featuredItem.onPress();
          } else if (isOffersItem && offersItem) {
            offersItem.onPress();
          } else {
            returnCategory(item.name);
          }
        }}
        isFeatured={isFeaturedItem}
        // encaminha flag para o Product caso deseje estilo específico para offers
        {...(isOffersItem ? {isOffers: true} : {})}
        // estilo adaptado para desktop para melhorar visualização em grid
        style={isDesktop ? {marginHorizontal: 8} : undefined}
      />
    );
  };

  const listContent = allItems && allItems.length > 0 ? (
    <FlatList
      ref={flatListRef}
      horizontal
      numColumns={undefined}
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
      data={allItems}
      renderItem={renderItem}
      keyExtractor={(item, index) =>
        item?.id != null ? item.id.toString() : index.toString()
      }
      contentContainerStyle={{paddingHorizontal: 16}}
      onScroll={e => setScrollOffset(e.nativeEvent.contentOffset.x)}
      scrollEventThrottle={16}
    />
  ) : (
    <Text>Nenhuma categoria encontrada</Text>
  );

  return (
    <View style={styles.container}>
      {!hasFetchedCategories ? (
        <SkeletonCategories isDesktop={isDesktop} />
      ) : isDesktop ? (
        <View style={arrowStyles.wrapper}>
          <TouchableOpacity
            onPress={scrollLeft}
            style={[arrowStyles.arrow, arrowStyles.arrowLeft, scrollOffset <= 0 && arrowStyles.arrowHidden]}
            activeOpacity={0.7}>
            <Text style={arrowStyles.arrowText}>{'‹'}</Text>
          </TouchableOpacity>
          <View style={arrowStyles.listWrapper}>
            {listContent}
          </View>
          <TouchableOpacity
            onPress={scrollRight}
            style={[arrowStyles.arrow, arrowStyles.arrowRight]}
            activeOpacity={0.7}>
            <Text style={arrowStyles.arrowText}>{'›'}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        listContent
      )}
    </View>
  );
};

const arrowStyles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  listWrapper: {
    flex: 1,
    overflow: 'hidden',
  },
  arrow: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0,0,0,0.08)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  arrowLeft: {
    marginRight: 4,
  },
  arrowRight: {
    marginLeft: 4,
  },
  arrowHidden: {
    opacity: 0,
    pointerEvents: 'none',
  } as any,
  arrowText: {
    fontSize: 20,
    lineHeight: 24,
    color: '#555',
    fontWeight: '300',
    marginTop: -2,
  },
});

const skeletonStyles = StyleSheet.create({
  skeletonItem: {
    alignItems: 'center',
    marginHorizontal: 12,
  },
  skeletonCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#e0e0e0',
  },
  skeletonText: {
    marginTop: 12,
    width: 60,
    height: 12,
    backgroundColor: '#e0e0e0',
    borderRadius: 6,
  },
});

export default ProductsCategories;